import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { visionInsuranceClaimFormEdit } from '../../../../../../../../redux/actions/addOns/rpa/vision_insurance/visionInsuranceClaimFormAction'

const display = (value) => {
  if (value === 0 || value === '0') return '0'
  if (!value) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'object') {
    if (value.label) return value.label
    if (value.LENS_OPTION_DESCRIPTION) return value.LENS_OPTION_DESCRIPTION
    if (value.LENS_TREATMENT_DESCRIPTION) return value.LENS_TREATMENT_DESCRIPTION
    if (value.LENS_MATERIAL_DESCRIPTION) return value.LENS_MATERIAL_DESCRIPTION
    if (value.LENS_STYLE_DESCRIPTION) return value.LENS_STYLE_DESCRIPTION
    if (value.DESCRIPTION) return `${value.DIAGNOSIS_CODE ? value.DIAGNOSIS_CODE + ' - ' : ''}${value.DESCRIPTION}`
    if (value.STYLE) return value.STYLE
    if (value.value !== undefined) return String(value.value)
    return JSON.stringify(value)
  }
  return String(value)
}

const displayCurrency = (value) => {
  if (value === 0 || value === '0') return '$0.00'
  if (!value) return '-'
  const num = Number(value)
  if (!isNaN(num)) return `$${num.toFixed(2)}`
  return String(value).startsWith('$') ? String(value) : `$${value}`
}

const OverviewField = ({ label, value, isCurrency = false, colClass = 'col-6 col-md-3' }) => {
  const formatted = isCurrency ? displayCurrency(value) : display(value)
  return (
    <div className={`${colClass} mt-4`}>
      <h6 className="mb-2 text-dark font-size-sm font-weight-bold">{label}</h6>
      <p className="text-muted mb-3 text-break">{formatted}</p>
    </div>
  )
}

const RxValue = ({ label, value }) => (
  <div className="col-6 col-md-3 mb-4">
    <div className="text-muted small mb-1">{label}</div>
    <div className="font-weight-bold text-break">{display(value)}</div>
  </div>
)

const getOptionLabel = (options = [], val) => {
  if (!val) return '-'
  if (typeof val === 'object') return val.label || val.value || val.DESCRIPTION || '-'
  const found = options.find((opt) => opt.value === val || opt.label === val)
  return found?.label || val
}

const getRxField = (eyeObj = {}, eyePrefix, suffix) => {
  return eyeObj[`${eyePrefix}_prescription_${suffix}`] ?? eyeObj[suffix]
}

export default function SpecteraClaimFormOverview(props) {
  const { visionInsuranceClaimForm = {}, claimFormTemplate } = props
  const dispatch = useDispatch()

  const portalType = useSelector(
    (state) =>
      state.visionInsurance?.services?.find((item) => item.config.service === 'spectera')?.config?.portal_type ||
      false
  )

  const {
    eligibility = {},
    diagnosis = {},
    exam = {},
    exams = {},
    contact_lens_fit = {},
    lab_frame = {},
    frame = {},
    spectera_lab = {},
    lab_prescription = {},
    lab_lenses = {},
    lens = {},
    contact = {},
    claim_data = {},
  } = visionInsuranceClaimForm

  const eligibilityData = eligibility?.data || []
  const activeServices = eligibilityData.filter((item) => item.active).map((item) => item.service_type)

  const showExam =
    activeServices.includes('Exam Benefit') ||
    Boolean(exam.procedure_code_1 || exams?.exam?.procedure_code_1)

  const showContactFit =
    activeServices.includes('Contact Fit') ||
    Boolean(contact_lens_fit.contact_fit_procedure_code)

  const hasLabFrame = Object.keys(lab_frame).length > 0
  const activeFrameData = hasLabFrame ? lab_frame : frame

  const showFrameBenefit =
    activeServices.includes('Frame Benefit') ||
    hasLabFrame ||
    Boolean(lab_frame.frame_bill_charges || frame.frame_bill_charges)

  const showLensBenefit =
    activeServices.includes('Lens Benefit') ||
    Boolean(
      lab_lenses.lens_style ||
      lens.lens_style ||
      lab_prescription.prescription_lens_type
    )

  const showContactBenefit =
    activeServices.includes('Contacts Benefit') ||
    Boolean(contact.left_style || contact.right_style || contact.left_billed_amount || contact.right_billed_amount)

  const examMap =
    claimFormTemplate?.config?.exam?.reduce((acc, item) => {
      acc[item.label] = item
      return acc
    }, {}) || {}

  const contactFitMap =
    claimFormTemplate?.config?.contact_lens_fit?.reduce((acc, item) => {
      acc[item.label] = item
      return acc
    }, {}) || {}

  const combinedExam = { ...exams?.exam, ...exam }
  const combinedDiagnosis = { ...exams?.diagnosis, ...diagnosis }

  const hasDiagnosis =
    Boolean(
      combinedDiagnosis.primary_diagnosis_code ||
      combinedDiagnosis.patient_referred_to_pcp
    )


  const activeLensData = portalType === 'lab' || Object.keys(lab_lenses).length > 0 ? lab_lenses : lens

  const rightRx = lab_prescription.right_prescription || {}
  const leftRx = lab_prescription.left_prescription || {}
  const hasPrescription =
    Boolean(lab_prescription.prescription_lens_type) ||
    Object.keys(rightRx).length > 0 ||
    Object.keys(leftRx).length > 0

  const charges = claim_data?.charges || []
  const totalCharges = charges.reduce((sum, item) => {
    const val = Number(item.charge)
    return isNaN(val) ? sum : sum + val
  }, 0)

  return (
    <div className="mt-7">
      <div className="bg-white p-6 border rounded">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-6 pb-4 border-bottom">
          <h3 className="mb-0 text-primary">Spectera Insurance Claim Form Overview</h3>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => dispatch(visionInsuranceClaimFormEdit({ formView: 'form', activeModule: 'first' }))}
          >
            Edit Claim Form
          </button>
        </div>

        {/* Selected Services */}
        <div className="mb-6 pb-5 border-bottom">
          <h5 className="mb-4 text-primary font-weight-bold">Selected Services</h5>
          <div className="d-flex flex-wrap">
            {activeServices.length > 0 ? (
              activeServices.map((service) => (
                <span key={service} className="badge badge-primary px-3 py-2 mr-2 mb-2">
                  {service}
                </span>
              ))
            ) : (
              <span className="text-muted">No services selected</span>
            )}
          </div>
        </div>

        {/* Diagnosis Information */}
        {hasDiagnosis && (
          <div className="mb-6 pb-5 border-bottom">
            <h5 className="mb-4 text-primary font-weight-bold">Diagnosis Codes</h5>
            <div className="row">
              <OverviewField
                label="Diagnosis Code 1 (Primary)"
                value={getOptionLabel(examMap?.primary_diagnosis_code?.options, combinedDiagnosis.primary_diagnosis_code)}
              />
              {combinedDiagnosis.diagnosis_code_2 && (
                <OverviewField
                  label="Diagnosis Code 2"
                  value={getOptionLabel(examMap?.primary_diagnosis_code?.options, combinedDiagnosis.diagnosis_code_2)}
                />
              )}
              {combinedDiagnosis.diagnosis_code_3 && (
                <OverviewField
                  label="Diagnosis Code 3"
                  value={getOptionLabel(examMap?.primary_diagnosis_code?.options, combinedDiagnosis.diagnosis_code_3)}
                />
              )}
              {combinedDiagnosis.diagnosis_code_4 && (
                <OverviewField
                  label="Diagnosis Code 4"
                  value={getOptionLabel(examMap?.primary_diagnosis_code?.options, combinedDiagnosis.diagnosis_code_4)}
                />
              )}
              {combinedDiagnosis.diagnosis_code_5 && (
                <OverviewField
                  label="Diagnosis Code 5"
                  value={getOptionLabel(examMap?.primary_diagnosis_code?.options, combinedDiagnosis.diagnosis_code_5)}
                />
              )}
              {combinedDiagnosis.diagnosis_code_6 && (
                <OverviewField
                  label="Diagnosis Code 6"
                  value={getOptionLabel(examMap?.primary_diagnosis_code?.options, combinedDiagnosis.diagnosis_code_6)}
                />
              )}
              {combinedDiagnosis.diagnosis_code_7 && (
                <OverviewField
                  label="Diagnosis Code 7"
                  value={getOptionLabel(examMap?.primary_diagnosis_code?.options, combinedDiagnosis.diagnosis_code_7)}
                />
              )}
              {combinedDiagnosis.diagnosis_code_8 && (
                <OverviewField
                  label="Diagnosis Code 8"
                  value={getOptionLabel(examMap?.primary_diagnosis_code?.options, combinedDiagnosis.diagnosis_code_8)}
                />
              )}
              <OverviewField
                label="Patient Referred to PCP"
                value={
                  combinedDiagnosis.patient_referred_to_pcp
                    ? combinedDiagnosis.patient_referred_to_pcp === 'yes'
                      ? 'Yes'
                      : 'No'
                    : '-'
                }
              />
              {combinedDiagnosis.patient_referred_to_pcp === 'yes' && (
                <OverviewField
                  label="Referral Diagnosis Code"
                  value={display(combinedDiagnosis.referral_diagnosis_code)}
                />
              )}
            </div>
          </div>
        )}

        {/* Exam Benefit Details */}
        {showExam && (
          <div className="mb-6 pb-5 border-bottom">
            <h5 className="mb-4 text-primary font-weight-bold">Exam Benefit Details</h5>
            <div className="row">
              <OverviewField
                label="Procedure Code 1"
                value={getOptionLabel(examMap?.procedure_code_1?.options, combinedExam.procedure_code_1)}
              />
              <OverviewField
                label="Procedure Code 1 Bill"
                value={combinedExam.procedure_code_1_bill}
                isCurrency
              />
              {combinedExam.procedure_code_2 && (
                <>
                  <OverviewField
                    label="Procedure Code 2"
                    value={getOptionLabel(examMap?.procedure_code_2?.options, combinedExam.procedure_code_2)}
                  />
                  <OverviewField
                    label="Procedure Code 2 Bill"
                    value={combinedExam.procedure_code_2_bill}
                    isCurrency
                  />
                </>
              )}
              {combinedExam.procedure_code_3 && (
                <>
                  <OverviewField
                    label="Procedure Code 3"
                    value={getOptionLabel(examMap?.procedure_code_3?.options, combinedExam.procedure_code_3)}
                  />
                  <OverviewField
                    label="Procedure Code 3 Bill"
                    value={combinedExam.procedure_code_3_bill}
                    isCurrency
                  />
                </>
              )}
              {combinedExam.patient_tested_for_glaucoma && (
                <OverviewField
                  label="Patient Tested for Glaucoma"
                  value={combinedExam.patient_tested_for_glaucoma === 'yes' ? 'Yes' : 'No'}
                />
              )}
              {combinedExam.patient_known_to_have_diabetes && (
                <OverviewField
                  label="Known to Have Diabetes"
                  value={combinedExam.patient_known_to_have_diabetes === 'yes' ? 'Yes' : 'No'}
                />
              )}
              {combinedExam.patient_known_to_have_diabetes === 'yes' && (
                <>
                  <OverviewField
                    label="Diabetes Diagnosis Code"
                    value={display(combinedExam.diabetes_diagnosis_code)}
                  />
                  <OverviewField
                    label="CPT II Code"
                    value={display(combinedExam.cptii_code)}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* Contact Fit Details */}
        {showContactFit && (
          <div className="mb-6 pb-5 border-bottom">
            <h5 className="mb-4 text-primary font-weight-bold">Contact Fit Details</h5>
            <div className="row">
              <OverviewField
                label="Procedure Code"
                value={getOptionLabel(contactFitMap?.contact_fit_procedure_code?.options, contact_lens_fit.contact_fit_procedure_code)}
              />
              <OverviewField
                label="Modifier"
                value={getOptionLabel(contactFitMap?.contact_fit_modifier?.options, contact_lens_fit.contact_fit_modifier)}
              />
              <OverviewField
                label="Billed Charges"
                value={contact_lens_fit.contact_fit_bill}
                isCurrency
              />
            </div>
          </div>
        )}

        {/* Frames Benefit Details */}
        {showFrameBenefit && (
          <div className="mb-6 pb-5 border-bottom">
            <h5 className="mb-4 text-primary font-weight-bold">
              {activeServices.includes('Frame Benefit') ? 'Frames Benefit Details' : 'Patient Frame Not Found Information'}
            </h5>
            {portalType === 'lab' || hasLabFrame ? (
              <div className="row">
                <OverviewField label="Manufacturer" value={activeFrameData.manufacturer} />
                <OverviewField label="Collection" value={activeFrameData.collection} />
                <OverviewField label="Style" value={activeFrameData.style} />
                {activeFrameData.model && <OverviewField label="Model" value={activeFrameData.model} />}
                <OverviewField label="Color" value={activeFrameData.color} />
                <OverviewField label="Eye Size" value={activeFrameData.eye_size} />
                <OverviewField label="B Measurement" value={activeFrameData.b_measurement} />
                <OverviewField label="Bridge Size" value={activeFrameData.bridge_size} />
                <OverviewField label="Temple Size" value={activeFrameData.temple_size} />
                <OverviewField label="Edge Type" value={activeFrameData.edge_type} />
                {spectera_lab.lab_name && (
                  <OverviewField label="Lab Selection" value={spectera_lab.lab_name} />
                )}
                <OverviewField
                  label={activeServices.includes('Frame Benefit') ? 'Frame Bill Charges' : 'Billed Charges'}
                  value={activeFrameData.frame_bill_charges}
                  isCurrency
                />
              </div>
            ) : (
              <div className="row">
                <OverviewField
                  label="Frame Bill Charges"
                  value={activeFrameData.frame_bill_charges}
                  isCurrency
                />
              </div>
            )}
          </div>
        )}

        {/* Lens Benefit Details */}
        {showLensBenefit && (
          <div className="mb-6 pb-5 border-bottom">
            <h5 className="mb-4 text-primary font-weight-bold">Lens Benefit Details</h5>

            {/* Prescription (Lab) */}
            {hasPrescription && (
              <div className="mb-5">
                <h6 className="mb-4 font-weight-bold">Prescription Information</h6>
                <div className="row mb-4">
                  <OverviewField
                    label="Lens / Prescription Type"
                    value={lab_prescription.prescription_lens_type}
                    colClass="col-12 col-md-6"
                  />
                </div>
                <div className="row">
                  {/* Right (OD) */}
                  <div className="col-12 col-lg-6 mb-4">
                    <div className="border rounded p-4 h-100 bg-light">
                      <h6 className="mb-4 font-weight-bold text-dark">Right Eye (OD)</h6>
                      <div className="row">
                        <RxValue label="Sphere" value={getRxField(rightRx, 'right', 'sphere')} />
                        <RxValue label="Cylinder" value={getRxField(rightRx, 'right', 'cylinder')} />
                        <RxValue label="Axis" value={getRxField(rightRx, 'right', 'axis')} />
                        <RxValue label="Addition" value={getRxField(rightRx, 'right', 'addition')} />
                        <RxValue label="Segment Height" value={getRxField(rightRx, 'right', 'segment_height')} />
                        <RxValue label="Optical Center" value={getRxField(rightRx, 'right', 'optical_center')} />
                        <RxValue label="OC Reference" value={getRxField(rightRx, 'right', 'oc_reference')} />
                        <RxValue label="Base Curve" value={getRxField(rightRx, 'right', 'base_curve')} />
                        <RxValue label="Distance PD" value={getRxField(rightRx, 'right', 'distance_pd')} />
                        <RxValue label="Near PD" value={getRxField(rightRx, 'right', 'near_pd')} />
                        <RxValue
                          label="Prism (H)"
                          value={
                            getRxField(rightRx, 'right', 'horizontal_prism')
                              ? `${display(getRxField(rightRx, 'right', 'horizontal_prism'))} ${display(getRxField(rightRx, 'right', 'horizontal_base'))}`
                              : '-'
                          }
                        />
                        <RxValue
                          label="Prism (V)"
                          value={
                            getRxField(rightRx, 'right', 'vertical_prism')
                              ? `${display(getRxField(rightRx, 'right', 'vertical_prism'))} ${display(getRxField(rightRx, 'right', 'vertical_base'))}`
                              : '-'
                          }
                        />
                        <RxValue label="Balance" value={getRxField(rightRx, 'right', 'balance') ? 'Yes' : 'No'} />
                      </div>
                    </div>
                  </div>

                  {/* Left (OS) */}
                  <div className="col-12 col-lg-6 mb-4">
                    <div className="border rounded p-4 h-100 bg-light">
                      <h6 className="mb-4 font-weight-bold text-dark">Left Eye (OS)</h6>
                      <div className="row">
                        <RxValue label="Sphere" value={getRxField(leftRx, 'left', 'sphere')} />
                        <RxValue label="Cylinder" value={getRxField(leftRx, 'left', 'cylinder')} />
                        <RxValue label="Axis" value={getRxField(leftRx, 'left', 'axis')} />
                        <RxValue label="Addition" value={getRxField(leftRx, 'left', 'addition')} />
                        <RxValue label="Segment Height" value={getRxField(leftRx, 'left', 'segment_height')} />
                        <RxValue label="Optical Center" value={getRxField(leftRx, 'left', 'optical_center')} />
                        <RxValue label="OC Reference" value={getRxField(leftRx, 'left', 'oc_reference')} />
                        <RxValue label="Base Curve" value={getRxField(leftRx, 'left', 'base_curve')} />
                        <RxValue label="Distance PD" value={getRxField(leftRx, 'left', 'distance_pd')} />
                        <RxValue label="Near PD" value={getRxField(leftRx, 'left', 'near_pd')} />
                        <RxValue
                          label="Prism (H)"
                          value={
                            getRxField(leftRx, 'left', 'horizontal_prism')
                              ? `${display(getRxField(leftRx, 'left', 'horizontal_prism'))} ${display(getRxField(leftRx, 'left', 'horizontal_base'))}`
                              : '-'
                          }
                        />
                        <RxValue
                          label="Prism (V)"
                          value={
                            getRxField(leftRx, 'left', 'vertical_prism')
                              ? `${display(getRxField(leftRx, 'left', 'vertical_prism'))} ${display(getRxField(leftRx, 'left', 'vertical_base'))}`
                              : '-'
                          }
                        />
                        <RxValue label="Balance" value={getRxField(leftRx, 'left', 'balance') ? 'Yes' : 'No'} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Lenses Selection Information */}
            <h6 className="mb-4 font-weight-bold">Lens Selection</h6>
            <div className="row">
              {activeLensData.lens_type && (
                <OverviewField label="Lens Type" value={activeLensData.lens_type} />
              )}
              <OverviewField label="Lens Style" value={activeLensData.lens_style} />
              <OverviewField
                label="Lens Style Bill"
                value={activeLensData.billed_charges ?? activeLensData.lens_style_bill_amount}
                isCurrency
              />
              <OverviewField
                label="Lens Material"
                value={activeLensData.material ?? activeLensData.lens_material}
              />
              <OverviewField
                label="Lens Material Bill"
                value={activeLensData.material_billed_charges ?? activeLensData.lens_material_bill_amount}
                isCurrency
              />
              <OverviewField
                label="Lens Treatment"
                value={activeLensData.treatment ?? activeLensData.lens_treatment}
              />
              <OverviewField
                label="Lens Treatment Bill"
                value={activeLensData.treatment_bill_charge ?? activeLensData.lens_treatment_bill_amount}
                isCurrency
              />
              {activeLensData.industrial_thickness && (
                <OverviewField
                  label="Industrial Thickness"
                  value={activeLensData.industrial_thickness}
                />
              )}
              {[1, 2, 3, 4, 5].map((num) => {
                const optionVal = activeLensData[`options_${num}`]
                const billVal = activeLensData[`options_${num}_bill_amount`]
                if (!optionVal && !billVal) return null
                return (
                  <React.Fragment key={num}>
                    <OverviewField
                      label={`Lens Option ${num}`}
                      value={optionVal}
                    />
                    <OverviewField
                      label={`Lens Option ${num} Bill`}
                      value={billVal}
                      isCurrency
                    />
                  </React.Fragment>
                )
              })}
            </div>
          </div>
        )}

        {/* Contacts Benefit Details */}
        {showContactBenefit && (
          <div className="mb-6 pb-5 border-bottom">
            <h5 className="mb-4 text-primary font-weight-bold">Contacts Benefit Details</h5>
            <div className="row">
              {/* Left Eye (OS) */}
              <div className="col-12 col-lg-6 mb-4">
                <div className="border rounded p-4 h-100 bg-light">
                  <h6 className="mb-4 font-weight-bold text-dark">Left Eye (OS)</h6>
                  <div className="row">
                    <OverviewField label="Style" value={contact.left_style} colClass="col-6" />
                    <OverviewField label="Color" value={contact.left_color} colClass="col-6" />
                    <OverviewField label="Base Curve" value={contact.left_base_curve} colClass="col-6" />
                    <OverviewField label="Diameter" value={contact.left_diameter} colClass="col-6" />
                    <OverviewField label="Qty / Boxes" value={contact.left_qtyl_boxes} colClass="col-6" />
                    <OverviewField
                      label="Billed Charges"
                      value={contact.left_billed_amount}
                      isCurrency
                      colClass="col-6"
                    />
                  </div>
                </div>
              </div>

              {/* Right Eye (OD) */}
              <div className="col-12 col-lg-6 mb-4">
                <div className="border rounded p-4 h-100 bg-light">
                  <h6 className="mb-4 font-weight-bold text-dark">Right Eye (OD)</h6>
                  <div className="row">
                    <OverviewField label="Style" value={contact.right_style} colClass="col-6" />
                    <OverviewField label="Color" value={contact.right_color} colClass="col-6" />
                    <OverviewField label="Base Curve" value={contact.right_base_curve} colClass="col-6" />
                    <OverviewField label="Diameter" value={contact.right_diameter} colClass="col-6" />
                    <OverviewField label="Qty / Boxes" value={contact.right_qtyl_boxes} colClass="col-6" />
                    <OverviewField
                      label="Billed Charges"
                      value={contact.right_billed_amount}
                      isCurrency
                      colClass="col-6"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Charges Table */}
        {charges.length > 0 && (
          <div className="my-6">
            <h5 className="mb-4 text-primary font-weight-bold">Charges</h5>
            <div className="table-responsive">
              <table className="table table-bordered table-striped">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: '60px' }}>S.No</th>
                    <th>Vision Care Service or Material</th>
                    <th style={{ width: '150px' }}>Usual Charge</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.service || '-'}</td>
                      <td>{displayCurrency(item.charge)}</td>
                    </tr>
                  ))}
                  <tr className="font-weight-bold bg-light">
                    <td colSpan={2} className="text-right">
                      Total Charges:
                    </td>
                    <td>{displayCurrency(totalCharges)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
